/**
 * Remove punctuation from a string. Optionally replace with a custom string.
 * @param str String to remove punctuation from
 * @param opt Object contains options for the function
 * @param opt.replacement String to replace punctuation with
 * @param opt.noTrim Do not trim the string before removing punctuation
 */
export const removePunctuation = (str: string = '', opt?: { replacement?: string, noTrim?: boolean }): string => {
	const { noTrim = false, replacement = '' } = opt || {};

	const word = noTrim ? str : str.trim();

	return word.replace(/[^a-zA-Z0-9\s]+/gi, replacement);
};

/**
 * Remove whitespace from a string. Optionally replace with a custom string.
 * @param str String to remove whitespace from
 * @param replacement String to replace whitespace with
 */
export const removeWhitespace = (str: string = '', replacement: string = ''): string => {
	return str.trim().replace(/\s+/g, replacement);
};

/**
 * Remove non-word characters from the end of a string.
 * @param str String to remove non-word characters from
 */
export const trimNonWord = (str: string = ''): string => {
	return str.replace(/[^a-zA-Z0-9\s]+$/g, '');
};

/**
 * Convert a string to camelCase.
 * @param str String to convert to camelCase
 * @param noTrim Do not trim the string before converting to camelCase
 */
export const camelCase = (str: string = '', noTrim?: boolean): string => {
	const camel = str.replace(/[\s\W\d_]+[a-z]/g, (w) => w.toUpperCase());
	return removePunctuation(camel, { noTrim });
};

/**
 * Convert a string to PascalCase.
 * @param str String to convert to PascalCase
 * @param noTrim Do not trim the string before converting to PascalCase
 */
export const pascalCase = (str: string = '', noTrim?: boolean): string => {
	const camel = camelCase(str, noTrim);

	return camel[0].toUpperCase() + camel.slice(1);
};

/**
 * Convert a string to Capital Case.
 * @param str String to convert to Capital Case
 * @param noTrim Do not trim the string before converting to Capitalcase
 */
export const capitalCase = (str: string = '', noTrim?: boolean): string => {
	const pascal = pascalCase(str, noTrim);

	return pascal.replace(/[a-z][A-Z0-9]|[0-9][A-Z]/g, (str) => `${str[0]} ${str[1]}`);
};

/**
 * Convert a string to kebab-case.
 * @param str String to convert to kebab-case
 * @param noTrim Do not trim the string before converting to kebab-case
 */
export const kebabCase = (str: string = '', noTrim: boolean = false): string => {
	const kebab = removePunctuation(str, { replacement: '-', noTrim }).replace(/[\s]+/g, '-');

	return kebab.replace(/[a-z][A-Z0-9]|[0-9][a-zA-Z]/g, (str) => `${str[0]}-${str[1]}`).toLowerCase();
};

/**
 * Convert a string to snake_case.
 * @param str String to convert to snake_case
 * @param noTrim Do not trim the string before converting to snake_case
 */
export const snakeCase = (str: string = '', noTrim: boolean = false): string => {
	const snake = removePunctuation(str, { replacement: '_', noTrim }).replace(/\s+/g, '_');

	return snake.replace(/[a-z][A-Z0-9]|[0-9][a-zA-Z]/g, (str) => `${str[0]}_${str[1]}`).toLowerCase();
};
